import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateFreeThing from "./UpdateFreeThing";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewFreeThing = ({ onFreeThingUpdated }) => {
  const [freeThings, setFreeThings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFreeThing, setEditingFreeThing] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    subtitle: "",
    description: "",
    download_link: "",
    icon: "",
    button_text: "",
    button_icon: ""
  });

  const fetchFreeThings = async () => {
    try {
      const response = await axios.get(`${apiUrl}/freethings`);
      setFreeThings(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load Free Things");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFreeThings();
  }, []);

  const handleEdit = (freeThing) => {
    setEditingFreeThing(freeThing.id);
    setFormData({
      slug: freeThing.slug,
      title: freeThing.title,
      subtitle: freeThing.subtitle,
      description: freeThing.description,
      download_link: freeThing.download_link,
      icon: freeThing.icon,
      button_text: freeThing.button_text,
      button_icon: freeThing.button_icon
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/freethingsupdate/${editingFreeThing}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onFreeThingUpdated) {
        onFreeThingUpdated();
      }
      await fetchFreeThings();
      setEditingFreeThing(null);
      alert("Free Thing updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the Free Thing.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Free Thing?")) return;
    
    try {
      await axios.delete(`${apiUrl}/free-things/${id}`);
      setFreeThings(freeThings.filter(item => item.id !== id));
      alert("Free Thing deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete Free Thing");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading Free Things...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Free Things</h2>
      </div>

      {freeThings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No Free Things found. Create your first Free Thing.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Download Link</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {freeThings.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500">{item.subtitle}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 line-clamp-2">{item.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a href={item.download_link} className="text-blue-600 hover:underline text-sm">
                      Download
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingFreeThing && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateFreeThing
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingFreeThing(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewFreeThing;